/**
 * Barre "view-as expert" : visible uniquement pour les UPN admin (cf view-as.ts).
 * Permet de simuler la vue d'un expert specifique dans le BO. La selection est
 * persistee en localStorage et propagee aux query keys via useEffect.
 */
import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, X } from 'lucide-react';
import { api, type Expert } from '../lib/api';
import { useViewAsExpert, isAdminUpn } from '../lib/view-as';
import type { SwaUser } from '../lib/auth';

interface Props {
  user: SwaUser | null;
}

export function ImpersonationBar({ user }: Props) {
  const isAdmin = isAdminUpn(user?.userDetails);
  const { expertId, setExpertId } = useViewAsExpert();
  const qc = useQueryClient();

  // Invalide les queries quand la selection change pour forcer un refetch
  // sur les pages courantes (RdvList, Dashboard, etc.).
  useEffect(() => {
    qc.invalidateQueries();
  }, [expertId, qc]);

  const { data: expertsData } = useQuery({
    queryKey: ['experts'],
    queryFn: () => api.expertsList(),
    enabled: isAdmin,
  });
  const experts: Expert[] = useMemo(() => expertsData?.items ?? [], [expertsData]);
  const currentLabel =
    expertId ? experts.find((e) => e.id === expertId)?.shortName ?? expertId : 'Vue admin';

  if (!isAdmin) return null;

  return (
    <div
      className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition ${
        expertId
          ? 'border-accent-gold/60 bg-accent-gold/10 text-accent-gold'
          : 'border-white/10 bg-primary-bg/60 text-cream/70'
      }`}
      title="View as expert (admin only)"
    >
      <Eye size={13} />
      <label className="font-medium uppercase tracking-wider opacity-80">View as</label>
      <select
        value={expertId ?? ''}
        onChange={(e) => setExpertId(e.target.value || null)}
        className="bg-transparent text-cream focus:outline-none"
      >
        <option value="">Vue admin (tout)</option>
        {experts.map((e) => (
          <option key={e.id} value={e.id} className="bg-primary-surface text-cream">
            {e.shortName}
          </option>
        ))}
      </select>
      {expertId && (
        <button
          type="button"
          onClick={() => setExpertId(null)}
          className="ml-1 rounded-full p-0.5 transition hover:bg-white/10"
          aria-label="Retour vue admin"
        >
          <X size={12} />
        </button>
      )}
      <span className="sr-only">Actuellement : {currentLabel}</span>
    </div>
  );
}
